import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { orderCampaignApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';

import diabetesImg from '../assets/Diabetes.webp';
import thyroidImg from '../assets/Thyroid.webp';
import hypertensionImg from '../assets/Hypertension.webp';
import digestiveImg from '../assets/Digestive.webp';
import pilesImg from '../assets/Piles.webp';
import backKneePainImg from '../assets/Back & Knee Pain.webp';
import asthmaImg from '../assets/Asthma.webp';
import psoriasisImg from '../assets/Psoriasis and Skin.webp';
import migraineImg from '../assets/Migraine.webp';
import anxietyStressImg from '../assets/Anxiety and Stress.webp';
import weightLossImg from '../assets/Weight Loss.webp';
import sinusAllergyImg from '../assets/Sinus & Allergy.webp';
import insomniaImg from '../assets/Insomnia Reversal.webp';
import liverDisorderImg from '../assets/Liver Disorder.webp';
import completeHeartImg from '../assets/Complete Heart.webp';
import pamsImg from '../assets/PCOD.webp';
import femaleInfertilityImg from '../assets/Female Infertility.webp';
import cervicalImg from '../assets/Cervical.webp';
import sciaticaImg from '../assets/Sciatica.webp';
import sexualWellnessImg from '../assets/Sexual Wellness.webp';

const campaignImageMap = {
  'Diabetes': diabetesImg,
  'Thyroid': thyroidImg,
  'Hypertension': hypertensionImg,
  'Digestive': digestiveImg,
  'Piles': pilesImg,
  'Back & Knee Pain': backKneePainImg,
  'Asthma': asthmaImg,
  'Psoriasis and Skin': psoriasisImg,
  'Migraine': migraineImg,
  'Anxiety and Stress': anxietyStressImg,
  'Weight Loss': weightLossImg,
  'Sinus & Allergy': sinusAllergyImg,
  'Insomnia Reversal': insomniaImg,
  'Liver Disorder': liverDisorderImg,
  'Complete Heart': completeHeartImg,
  'PCOS/PCOD': pamsImg,
  'Female Infertility': femaleInfertilityImg,
  'Cervical': cervicalImg,
  'Sciatica': sciaticaImg,
  'Sexual Wellness': sexualWellnessImg,
};

const colorPalette = [
  'bg-cyan-100', 'bg-teal-100', 'bg-orange-100', 'bg-blue-100',
  'bg-red-100', 'bg-yellow-100', 'bg-green-100', 'bg-emerald-100',
  'bg-pink-100', 'bg-purple-100', 'bg-lime-100', 'bg-indigo-100',
  'bg-amber-100', 'bg-rose-100', 'bg-fuchsia-100', 'bg-violet-100',
];

const CampaignSelectionModal = ({ isOpen, onClose, onSubmit, loading }) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchCampaigns();
    }
  }, [isOpen]);

  const fetchCampaigns = async () => {
    try {
      setFetchLoading(true);
      setFetchError(null);

      const campaignsData = await orderCampaignApi.getAllCampaigns();

      const transformedCampaigns = campaignsData.map((item, index) => ({
        id: item.id.toString(),
        name: item.campaignName,
        color: colorPalette[index % colorPalette.length],
        image: campaignImageMap[item.campaignName] || null,
      }));

      setCampaigns(transformedCampaigns);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setFetchError(err.message || 'Failed to load campaigns.');
    } finally {
      setFetchLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm p-4">
      <div className={`rounded-3xl w-full max-w-6xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh] border transition-all duration-500 ${isDark ? 'bg-[#0B0F19] border-white/10' : 'bg-white border-gray-100'}`}>

        {/* Header */}
        <div className={`px-8 py-6 flex items-center justify-between border-b flex-shrink-0 transition-all`} style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC', borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#F1F5F9' }}>
          <div>
            <h3 className={`text-xl font-black uppercase tracking-[0.3em] ${isDark ? 'text-white' : 'text-gray-800'}`}>Select Campaign</h3>
            <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Choose a campaign to assign leads</p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl transition-all ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-400'}`}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className={`p-8 overflow-y-auto flex-grow transition-all [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${isDark ? 'bg-[#0B0F19]' : 'bg-white'}`}>
          {fetchLoading ? (
            <div className="flex items-center justify-center h-60">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: currentTheme.color }}></div>
                <p className="text-gray-500 font-black text-xs uppercase tracking-[0.2em] animate-pulse">Fetching Campaigns...</p>
              </div>
            </div>
          ) : fetchError ? (
            <div className="flex flex-col items-center justify-center h-60 gap-4">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center text-red-500">
                <X className="w-8 h-8" />
              </div>
              <p className="text-red-500 font-black uppercase tracking-widest text-sm">{fetchError}</p>
              <button onClick={fetchCampaigns} className="px-6 py-2 rounded-xl font-black text-[10px] text-white uppercase tracking-widest transition-all hover:scale-105 active:scale-95" style={{ backgroundColor: currentTheme.color }}>Retry Fetch</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {campaigns.map((campaign) => {
                const isSelected = selectedCampaign === campaign.name;
                return (
                  <div
                    key={campaign.name}
                    onClick={() => setSelectedCampaign(isSelected ? null : campaign.name)}
                    className={`
                      group relative flex items-center gap-4 p-4 rounded-2xl cursor-pointer border-2 transition-all duration-300
                      ${isSelected
                        ? 'shadow-[0_20px_50px_rgba(0,0,0,0.2)] scale-[1.02] z-10'
                        : (isDark ? 'border-white/5 bg-white/5 hover:border-white/10 hover:bg-white/10' : 'border-gray-50 bg-gray-50 hover:border-gray-200 hover:bg-gray-100/50')
                      }
                    `}
                    style={isSelected ? { borderColor: currentTheme.color, backgroundColor: isDark ? `${currentTheme.color}20` : 'white' } : {}}
                  >
                    {/* Icon Container */}
                    <div className={`w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center transition-all duration-500 group-hover:rotate-6 ${isSelected ? 'shadow-lg' : (isDark ? 'bg-white/10' : campaign.color)}`}
                      style={isSelected ? { backgroundColor: currentTheme.color } : {}}>
                      {campaign.image ? (
                        <img src={campaign.image} alt={campaign.name} className="w-8 h-8 object-contain filter brightness-100 invert-0" style={isSelected ? { filter: 'brightness(0) invert(1)' } : {}} />
                      ) : (
                        <span className={`text-sm font-black ${isSelected ? 'text-white' : (isDark ? 'text-gray-300' : 'text-gray-600')}`}>{campaign.name.substring(0, 1)}</span>
                      )}
                    </div>

                    {/* Text Container */}
                    <div className="flex-1 min-w-0">
                      <p className={`font-black text-[10px] uppercase tracking-widest truncate transition-colors ${isSelected ? (isDark ? 'text-white' : 'text-gray-900') : (isDark ? 'text-gray-400' : 'text-gray-600')}`}>
                        {campaign.name}
                      </p>
                      {isSelected && <p className="text-[8px] font-black uppercase tracking-widest text-teal-500 mt-0.5 animate-pulse">Selected</p>}
                    </div>

                    {/* Toggle Switch */}
                    <div className={`w-10 h-5 flex items-center rounded-full p-1 transition-all duration-500 flex-shrink-0 ${isSelected ? '' : (isDark ? 'bg-gray-800' : 'bg-gray-300')}`}
                      style={isSelected ? { backgroundColor: currentTheme.color, boxShadow: `0 0 15px ${currentTheme.color}40` } : {}}>
                      <div className={`w-3 h-3 rounded-full shadow-md transform transition-all duration-500 ${isSelected ? 'translate-x-5 bg-white scale-110' : 'translate-x-0 bg-gray-500'}`}></div>
                    </div>

                    {/* Glow effect for selected card */}
                    {isSelected && (
                      <div className="absolute -inset-1 rounded-2xl blur-xl opacity-20 transition-opacity" style={{ backgroundColor: currentTheme.color }}></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-8 border-t flex items-center justify-between flex-shrink-0 transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-gray-50'}`}>
          <div className="hidden sm:block">
            {selectedCampaign && (
              <p className={`text-[10px] font-black uppercase tracking-[0.2em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Assigning to: <span style={{ color: currentTheme.color }}>{selectedCampaign}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <button onClick={onClose} className={`flex-1 sm:flex-none px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button
              onClick={() => onSubmit(selectedCampaign)}
              disabled={!selectedCampaign || loading}
              className={`flex-1 sm:flex-none px-12 py-3 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all shadow-xl active:scale-95 disabled:opacity-30 disabled:grayscale disabled:scale-100 disabled:cursor-not-allowed text-white`}
              style={(!selectedCampaign || loading) ? {} : { backgroundColor: currentTheme.color, boxShadow: `0 10px 30px -10px ${currentTheme.color}` }}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Processing</span>
                </div>
              ) : 'Confirm Assignment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignSelectionModal;